import { format, Locale, parseISO } from 'date-fns';
import { de, enUS, es, fr, ja, ko } from 'date-fns/locale';
import React, { useCallback, useImperativeHandle, useRef } from 'react';
import ReactDatePicker, { ReactDatePickerProps, registerLocale } from 'react-datepicker';
import { devWarning } from '@tanium/coreui-utils';
import { css } from '@tanium/react-emotion-9';
import { useWorkbenchLocale } from '@tanium/react-locale-context';
import { useControlledProp } from '@tanium/react-use-controlled-prop';
import { LayoutStyleProps } from '@tanium/style-system';
import { calendarContainerFactory } from './calendarContainerFactory';
import { customHeaderRenderer } from './customHeaderRenderer';
import { DatePickerInput } from './DatePickerInput';
import { DatePickerSize } from './DatePickerSize';
import { InlineErrorBoundary } from './InlineErrorBoundary';
import { ResourceKey } from './localization';

export interface DatePickerChangeEvent {
  value: string;
}

export interface Props extends Pick<LayoutStyleProps, 'width'> {
  /**
   * Value of the DatePicker component. Using this prop makes the DatePicker a controlled component.
   * Values are of the format yyyy-MM-dd (per HTML spec of <input type="date"/>).
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
   * You can use the npm `date-fns` package to format dates.
   */
  value?: string;
  /**
   * Default value for the DatePicker. Used when the DatePicker is uncontrolled.
   * Values are of the format yyyy-MM-dd (per HTML spec of <input type="date"/>).
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
   * You can use the npm `date-fns` package to format dates.
   */
  defaultValue?: string;
  /**
   * Fired when the value of the DatePicker has been changed.
   * Values are of the format yyyy-MM-dd (per HTML spec of <input type="date"/>).
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
   */
  onChange?: (args: DatePickerChangeEvent) => void;
  /**
   * A function with a `Date` as an argument that returns a `boolean`. If provided, it will be called
   * for every calendar date rendered in the calendar view. If it returns `false`,
   * the calendar date will be disabled for selection.
   */
  filterDate?: (date: Date) => boolean;
  /**
   * Value for the DatePicker minimum date (Inclusive).
   * Values are of the format yyyy-MM-dd (per HTML spec of <input type="date"/>).
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
   * You can use the npm `date-fns` package to format dates.
   */
  minDate?: string;
  /**
   * Value for the DatePicker maximum date (Inclusive).
   * Values are of the format yyyy-MM-dd (per HTML spec of <input type="date"/>).
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
   * You can use the npm `date-fns` package to format dates.
   */
  maxDate?: string;
  /**
   * Popper modifiers to affect placement of the Calendar popover component.
   */
  popperModifiers?: React.ComponentProps<typeof ReactDatePicker>['popperModifiers'];
  /**
   * Whether the input should be disabled.
   */
  disabled?: boolean;
  /**
   * Allows a 'sm' or 'lg' size to be specified.
   */
  size?: DatePickerSize;
}

type ImperativeApi = Pick<ReactDatePicker, 'setOpen' | 'setFocus' | 'setBlur' | 'isCalendarOpen'>;

// This value mimics the CSS of the underlying library
const popperClassName = css({ zIndex: 1 });

const defaultFilterDate = () => true;

const isDayInRange = (day: Date, minDate?: Date, maxDate?: Date) => {
  if ((minDate && day < minDate) || (maxDate && day > maxDate)) {
    return false;
  }
  return true;
};

const localeDict: Record<ResourceKey, Locale> = {
  'fr-FR': fr,
  'de-DE': de,
  'es-LA': es,
  'ko-KR': ko,
  'ja-JP': ja,
  'en-US': enUS,
};
Object.entries(localeDict).forEach((pair) => registerLocale(...pair));

/**
 * Component used for selecting a single date.
 */
export const DatePicker = React.forwardRef<ImperativeApi, Props>(
  (
    {
      value,
      defaultValue,
      onChange,
      size,
      width,
      filterDate: filterDateProp,
      minDate: minDateProp,
      maxDate: maxDateProp,
      popperModifiers,
      disabled,
    },
    ref,
  ) => {
    const [dateValue, setDateValue] = useControlledProp({
      value,
      defaultValue,
      componentName: 'DatePicker',
      propName: 'value',
      onUpdate: ({ value: newValue }) => {
        if (onChange) {
          onChange({ value: newValue });
        }
      },
    });
    const workbenchLocale = useWorkbenchLocale();

    const datePickerRef = useRef<ReactDatePicker>(null);

    useImperativeHandle(ref, () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { setBlur, setFocus, setOpen, isCalendarOpen } = datePickerRef.current!;
      return { setBlur, setFocus, setOpen, isCalendarOpen };
    });

    const updateDate = useCallback(
      (v: Date) => {
        const formattedDate = format(v, 'yyyy-MM-dd');
        setDateValue(formattedDate);
      },
      [setDateValue],
    );

    const handleChangeDate = useCallback(
      (v: Date | null) => {
        if (!v) return;
        updateDate(v);
      },
      [updateDate],
    );

    const formatWeekDay = useCallback((day: string) => day[0], []);

    const parsedMinDate = minDateProp ? parseISO(minDateProp) : undefined;
    const parsedMaxDate = maxDateProp ? parseISO(maxDateProp) : undefined;

    const filterDate = filterDateProp || defaultFilterDate;
    const isValidDay = (v: Date) => filterDate(v) && isDayInRange(v, parsedMinDate, parsedMaxDate);

    // This is a little tricky. ReactDatePicker expects the exact same component, or it loses some
    // state in a strange way (interacting with the keyboard and then clicking a date will no longer
    // trigger a value change). We use a ref, instead of useCallback, so that we can guarantee the
    // same component is rendered each time.
    const containerRef = useRef(calendarContainerFactory(updateDate, datePickerRef, isValidDay));

    // https://github.com/Hacker0x01/react-datepicker/issues/1565: we enter an infinite loop if
    // consumers programmatically pass certain invalid values.
    devWarning(
      typeof (defaultValue || value) === 'string',
      `A DatePicker must receive either a defaultValue or a value in a valid string format ("yyyy-MM-dd"). Instead, received ${
        // eslint-disable-next-line no-nested-ternary
        defaultValue
          ? `defaultValue of "${defaultValue}"`
          : value
          ? `value of "${value}"`
          : 'neither a defaultValue or a value'
      }. You can use 'date-fns' to convert dates should the need arise; failure to supply a valid value can cause the DatePicker not to render.`,
    );

    const parsedDate = parseISO(dateValue);

    devWarning(
      !parsedMinDate || !parsedMaxDate || parsedMinDate <= parsedMaxDate,
      `A DatePicker's minDate value cannot be greater than the maxDate value. It received a minDate of ${minDateProp} and a maxDate of ${maxDateProp}`,
    );

    return (
      <InlineErrorBoundary as="span">
        <ReactDatePicker
          locale={workbenchLocale}
          ref={datePickerRef}
          selected={parsedDate}
          onChange={handleChangeDate}
          formatWeekDay={
            // This is a typing error in ReactDatePicker
            formatWeekDay as unknown as ReactDatePickerProps['formatWeekDay']
          }
          renderCustomHeader={customHeaderRenderer}
          calendarContainer={containerRef.current}
          popperModifiers={popperModifiers}
          customInput={<DatePickerInput size={size} width={width} />}
          dateFormat="P"
          popperClassName={popperClassName}
          filterDate={filterDate}
          minDate={parsedMinDate}
          maxDate={parsedMaxDate}
          disabled={disabled}
        />
      </InlineErrorBoundary>
    );
  },
);

DatePicker.displayName = 'DatePicker';
